/**
 * E-Prescription API
 *
 * RTK Query endpoints for e-prescription (DoseSpot) integration.
 * Enhances generated endpoints with proper cache invalidation.
 */

import { useCallback } from 'react';
import {
  enhancedApi,
  type GetV2ProviderEprescriptionsSsoApiArg,
} from '@store/generated/eprescriptions';

/**
 * Feature-specific tag types for cache invalidation
 */
const TAG_TYPES = [
  'EprescriptionStatus',
  'DeaNumbers',
  'PatientPrescribingReadiness',
] as const;

/**
 * Enhanced e-prescription API with cache management
 */
export const eprescriptionsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: TAG_TYPES,
  endpoints: {
    getV2ProviderEprescriptionsStatus: {
      providesTags: ['EprescriptionStatus'],
    },
    getV2ProviderEprescriptionsRegistrationStatus: {
      providesTags: ['EprescriptionStatus'],
    },
    postV2ProviderEprescriptionsSetupClinic: {
      invalidatesTags: ['EprescriptionStatus'],
    },
    postV2ProviderEprescriptionsSetupClinician: {
      invalidatesTags: ['EprescriptionStatus'],
    },
    postV2ProviderEprescriptionsSetupProxy: {
      invalidatesTags: ['EprescriptionStatus'],
    },
    postV2ProviderPatientsByPatientIdEprescriptionsConnect: {
      invalidatesTags: (_result, _error, { patientId }) => [
        'EprescriptionStatus',
        { type: 'PatientPrescribingReadiness', id: patientId },
      ],
    },
    postV2ProviderPatientsByPatientIdEprescriptionsPrescribeIntent: {
      invalidatesTags: (_result, _error, { patientId }) => [
        'EprescriptionStatus',
        { type: 'PatientPrescribingReadiness', id: patientId },
      ],
    },
    getV2ProviderPatientsByPatientIdEprescriptionsReadiness: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPrescribingReadiness', id: patientId },
      ],
    },
    // DEA number CRUD
    getV2ProviderEprescriptionsDeaNumbers: {
      providesTags: ['DeaNumbers'],
    },
    postV2ProviderEprescriptionsDeaNumbers: {
      invalidatesTags: ['DeaNumbers'],
    },
    putV2ProviderEprescriptionsDeaNumbersByDeaId: {
      invalidatesTags: ['DeaNumbers'],
    },
    deleteV2ProviderEprescriptionsDeaNumbersByDeaId: {
      invalidatesTags: ['DeaNumbers'],
    },
  },
});

// Export with user-friendly names (following feature pattern guidelines)
export const {
  useGetV2ProviderEprescriptionsStatusQuery: useGetEPrescriptionStatusQuery,
  useGetV2ProviderEprescriptionsRegistrationStatusQuery: useGetRegistrationStatusQuery,
  usePostV2ProviderEprescriptionsSetupClinicMutation: useSetupClinicMutation,
  usePostV2ProviderEprescriptionsSetupClinicianMutation: useSetupClinicianMutation,
  usePostV2ProviderEprescriptionsSetupProxyMutation: useSetupProxyMutation,
  usePostV2ProviderPatientsByPatientIdEprescriptionsConnectMutation: useConnectPatientMutation,
  usePostV2ProviderPatientsByPatientIdEprescriptionsPrescribeIntentMutation: usePrescribeIntentMutation,
  useGetV2ProviderPatientsByPatientIdEprescriptionsReadinessQuery: useGetPatientPrescribingReadinessQuery,
  useLazyGetV2ProviderPatientsByPatientIdEprescriptionsReadinessQuery: useLazyGetPatientPrescribingReadinessQuery,
  useGetV2ProviderPatientsByPatientIdEprescriptionsSsoQuery: useGetPatientSsoQuery,
  useLazyGetV2ProviderPatientsByPatientIdEprescriptionsSsoQuery: useLazyGetPatientSsoQuery,
  // DEA number management
  useGetV2ProviderEprescriptionsDeaNumbersQuery: useGetDeaNumbersQuery,
  usePostV2ProviderEprescriptionsDeaNumbersMutation: useAddDeaNumberMutation,
  usePutV2ProviderEprescriptionsDeaNumbersByDeaIdMutation: useUpdateDeaNumberMutation,
  useDeleteV2ProviderEprescriptionsDeaNumbersByDeaIdMutation: useDeleteDeaNumberMutation,
  // Medication history (SureScripts) consent
  usePostV2ProviderPatientsByPatientIdMedicationHistoryConsentMutation: useRecordMedicationHistoryConsentMutation,
} = eprescriptionsApi;

const useLazyClinicianSsoRaw =
  eprescriptionsApi.useLazyGetV2ProviderEprescriptionsSsoQuery;

/**
 * Clinician SSO lazy query with an OPTIONAL trigger arg.
 *
 * Codegen makes the query-arg object required once an endpoint has any
 * parameters (we added the optional `on_behalf_of_provider_id`). Wrapping
 * the generated hook here keeps `getClinicianSso()` callable with no args,
 * so future regenerations never force call-site churn — the SSO query stays
 * backwards compatible regardless of added optional params.
 */
export function useLazyGetClinicianSsoQuery(
  ...hookArgs: Parameters<typeof useLazyClinicianSsoRaw>
) {
  const [trigger, ...rest] = useLazyClinicianSsoRaw(...hookArgs);
  const wrappedTrigger = useCallback(
    (
      arg?: GetV2ProviderEprescriptionsSsoApiArg,
      preferCacheValue?: boolean,
    ) => trigger(arg ?? {}, preferCacheValue),
    [trigger],
  );
  return [wrappedTrigger, ...rest] as [
    typeof wrappedTrigger,
    ...typeof rest,
  ];
}
